import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import {
  ArrowLeft, CheckCircle2, Circle, Trash2, Plus, X, Activity, BellRing,
  Paperclip, Upload, Download, FileText,
} from "lucide-react";
import { SourceSelect } from "../components/SourceSelect";
import { HostingSection } from "../components/HostingSection";

const CATEGORIES = [
  { key: "digital_marketing", label: "Digital Marketing" },
  { key: "creative_services", label: "Creative Services" },
  { key: "software_development", label: "Software Development" },
  { key: "deepthree_assist", label: "DeepThree Assist" },
  { key: "deepthree_deliver", label: "DeepThree Deliver" },
];

const IMPORTANCE_COLORS = {
  1: "#F59E0B",
  2: "#EA580C",
  3: "#DC2626",
  4: "#B91C1C",
  5: "#7F1D1D",
};
const IMPORTANCE_LABELS = ["", "!", "!!", "!!!", "!!!!", "!!!!!"];
const PLACEMENTS = [
  { key: "active", label: "Active Projects" },
  { key: "sales_pipeline", label: "Sales Pipeline" },
  { key: "passive", label: "Passive Projects" },
  { key: "completed", label: "Completed" },
];

export default function ProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [activity, setActivity] = useState([]);
  const [newTask, setNewTask] = useState("");
  const [sources, setSources] = useState([]);
  const [meta, setMeta] = useState(null);

  const load = async () => {
    const [projs, act] = await Promise.all([
      api.get("/projects"),
      api.get(`/projects/${id}/activity`),
    ]);
    setProject(projs.data.find((p) => p.id === id) || null);
    setActivity(act.data);
  };

  useEffect(() => { load(); }, [id]);
  useEffect(() => { api.get("/project-meta").then(({ data }) => { setMeta(data); setSources(data.sources || []); }).catch(() => {}); }, []);

  if (!project) return <div className="text-sm text-zinc-500" data-testid="project-detail-loading">Loading…</div>;

  const isComplete = project.status === "complete";

  const update = async (patch, silent = false) => {
    const next = { ...project, ...patch };
    setProject(next);
    try {
      await api.put(`/projects/${id}`, patch);
      if (!silent) toast.success("Saved");
      const { data } = await api.get(`/projects/${id}/activity`);
      setActivity(data);
    } catch {
      toast.error("Failed to save");
    }
  };

  const addTask = async () => {
    if (!newTask.trim()) return;
    const tasks = [...(project.tasks || []), { id: crypto.randomUUID(), title: newTask.trim(), completed: false }];
    setNewTask("");
    await update({ tasks });
  };
  const toggleTask = async (tid) => {
    const tasks = (project.tasks || []).map((t) => t.id === tid ? { ...t, completed: !t.completed } : t);
    await update({ tasks }, true);
  };
  const removeTask = async (tid) => {
    const tasks = (project.tasks || []).filter((t) => t.id !== tid);
    await update({ tasks });
  };

  const toggleStatus = async () => {
    await update(isComplete
      ? { status: "active", placement: project.placement === "completed" ? "active" : project.placement }
      : { status: "complete", placement: "completed", subscription_tier: "none", subscription_amount: 0 });
  };

  const remove = async () => {
    if (!isComplete) {
      toast.error("Mark as complete before deleting");
      return;
    }
    if (!window.confirm("Delete this project?")) return;
    try {
      await api.delete(`/projects/${id}`);
      toast.success("Deleted");
      navigate("/projects");
    } catch {
      toast.error("Failed to delete");
    }
  };

  const tasksDone = (project.tasks || []).filter((t) => t.completed).length;
  const tasksTotal = (project.tasks || []).length;
  const categories = meta?.categories || CATEGORIES;
  const categoryLabel = categories.find((c) => c.key === project.category)?.label || project.category;

  return (
    <div data-testid="project-detail-page" className={isComplete ? "opacity-90" : ""}>
      <button data-testid="project-back" onClick={() => navigate("/projects")} className="mb-6 inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-900">
        <ArrowLeft size={14} /> Back to projects
      </button>

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="text-xs font-semibold uppercase tracking-widest text-zinc-500">{categoryLabel}</div>
          <input
            data-testid="project-detail-title"
            value={project.title}
            onChange={(e) => setProject({ ...project, title: e.target.value })}
            onBlur={() => update({ title: project.title })}
            className="mt-1 w-full bg-transparent text-3xl font-semibold tracking-tight text-zinc-900 outline-none focus:bg-zinc-50 focus:rounded-md focus:px-2 focus:-mx-2"
          />
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <span
              className="rounded-full px-2.5 py-1 text-xs font-bold tracking-wider text-white"
              style={{ backgroundColor: IMPORTANCE_COLORS[project.importance ?? project.priority ?? 3] || IMPORTANCE_COLORS[3] }}
            >
              {IMPORTANCE_LABELS[project.importance ?? project.priority ?? 3] || "!!!"}
            </span>
            {parseFloat(project.expected_revenue || 0) > 0 && (
              <span className="rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700">
                ${parseFloat(project.expected_revenue).toLocaleString(undefined, { maximumFractionDigits: 0 })}
              </span>
            )}
            <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${isComplete ? "bg-emerald-50 text-emerald-700" : "bg-zinc-100 text-zinc-700"}`}>
              {isComplete ? "Complete" : "Active"}
            </span>
            {project.deadline && (
              <span className="rounded-full bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-700">
                Due {format(parseISO(project.deadline), "MMM d, yyyy")}
              </span>
            )}
          </div>
        </div>
        <div className="flex flex-shrink-0 items-center gap-2">
          <button
            data-testid="project-detail-toggle-status"
            onClick={toggleStatus}
            className={`inline-flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium ${
              isComplete
                ? "border border-zinc-200 bg-white text-zinc-700 hover:bg-zinc-50"
                : "bg-emerald-500 text-white hover:bg-emerald-600"
            }`}
          >
            <CheckCircle2 size={14} /> {isComplete ? "Reopen" : "Mark Complete"}
          </button>
          {isComplete && (
            <button data-testid="project-detail-delete" onClick={remove} className="inline-flex items-center gap-2 rounded-md border border-zinc-200 bg-white px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50">
              <Trash2 size={14} /> Delete
            </button>
          )}
        </div>
      </div>

      <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          {/* Details */}
          <div className="dt-card p-6">
            <h2 className="text-sm font-semibold tracking-tight text-zinc-900">Details</h2>
            <div className="mt-4 grid grid-cols-2 gap-4">
              <Field label="Type of work">
                <select value={project.category} onChange={(e) => update({ category: e.target.value })} className={inputCls}>
                  {categories.map((c) => (<option key={c.key} value={c.key}>{c.label}</option>))}
                </select>
              </Field>
              <Field label="Importance">
                <div data-testid="project-detail-importance" className="flex items-center gap-1">
                  {[1, 2, 3, 4, 5].map((n) => {
                    const active = (project.importance ?? project.priority ?? 3) === n;
                    return (
                      <button
                        key={n}
                        type="button"
                        data-testid={`project-detail-importance-${n}`}
                        onClick={() => update({ importance: n })}
                        className={`rounded-md px-2.5 py-1.5 text-xs font-bold tracking-wider transition-all ${active ? "text-white scale-[1.04]" : "border border-zinc-200 bg-white text-zinc-700 hover:bg-zinc-50"}`}
                        style={active ? { backgroundColor: IMPORTANCE_COLORS[n] } : undefined}
                      >
                        {IMPORTANCE_LABELS[n]}
                      </button>
                    );
                  })}
                </div>
              </Field>
              <Field label="Client Name">
                <input
                  data-testid="project-detail-client"
                  value={project.client_name || ""}
                  onChange={(e) => setProject({ ...project, client_name: e.target.value })}
                  onBlur={(e) => update({ client_name: e.target.value })}
                  className={inputCls}
                />
              </Field>
              <Field label="Expected revenue ($)">
                <input
                  data-testid="project-detail-revenue"
                  type="number"
                  step="0.01"
                  min="0"
                  value={project.expected_revenue ?? 0}
                  onChange={(e) => setProject({ ...project, expected_revenue: e.target.value })}
                  onBlur={(e) => update({ expected_revenue: parseFloat(e.target.value || 0) || 0 })}
                  className={inputCls}
                />
              </Field>
              <Field label="Deadline">
                <input data-testid="project-detail-deadline" type="date" value={project.deadline || ""} onChange={(e) => update({ deadline: e.target.value })} className={inputCls} />
              </Field>
              <Field label="Section">
                <select
                  data-testid="project-detail-placement"
                  value={project.placement || (isComplete ? "completed" : "active")}
                  onChange={(e) => {
                    const next = e.target.value;
                    const patch = { placement: next, status: next === "completed" ? "complete" : "active" };
                    if (next === "completed") { patch.subscription_tier = "none"; patch.subscription_amount = 0; }
                    update(patch);
                  }}
                  className={inputCls}
                >
                  {PLACEMENTS.map((p) => (<option key={p.key} value={p.key}>{p.label}</option>))}
                </select>
              </Field>
              <Field label="Lead source">
                <SourceSelect
                  testid="project-detail-source"
                  value={project.source || ""}
                  sources={sources}
                  onChange={(v) => {
                    if (v && !sources.some((s) => s.toLowerCase() === v.toLowerCase())) setSources([...sources, v]);
                    update({ source: v });
                  }}
                  className={inputCls}
                />
              </Field>
              <div className="col-span-2">
                <Field label="Last Updated">
                  <div className="rounded-md border border-zinc-100 bg-zinc-50 px-3 py-2 text-sm text-zinc-600">
                    {project.updated_at ? format(parseISO(project.updated_at), "MMM d, yyyy · p") : "—"}
                  </div>
                </Field>
              </div>
            </div>
          </div>

          <HostingSection
            project={project}
            meta={meta}
            onChange={(patch) => update(patch)}
            onLocalChange={(patch) => setProject({ ...project, ...patch })}
          />

          {/* Tasks */}
          <div className="dt-card p-6">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-semibold tracking-tight text-zinc-900">Tasks</h2>
              <span className="text-xs text-zinc-500">{tasksDone}/{tasksTotal}</span>
            </div>
            {tasksTotal > 0 && (
              <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-zinc-100">
                <div className="h-full bg-[#0066ff] transition-all" style={{ width: `${(tasksDone / Math.max(tasksTotal, 1)) * 100}%` }} />
              </div>
            )}
            <div className="mt-4 flex flex-col gap-1">
              {(project.tasks || []).map((t) => (
                <div key={t.id} data-testid={`project-task-${t.id}`} className="flex items-center justify-between rounded-md border border-zinc-100 px-3 py-2 text-sm">
                  <button onClick={() => toggleTask(t.id)} className="flex flex-1 items-center gap-2 text-left">
                    {t.completed ? <CheckCircle2 size={16} className="text-emerald-500" /> : <Circle size={16} className="text-zinc-400" />}
                    <span className={t.completed ? "text-zinc-400 line-through" : "text-zinc-800"}>{t.title}</span>
                  </button>
                  <button onClick={() => removeTask(t.id)} className="rounded p-1 text-zinc-400 hover:bg-zinc-100"><X size={12} /></button>
                </div>
              ))}
            </div>
            <div className="mt-3 flex gap-2">
              <input data-testid="project-detail-new-task" value={newTask} onChange={(e) => setNewTask(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addTask()} placeholder="Assign yourself a task" className={inputCls} />
              <button data-testid="project-detail-add-task" onClick={addTask} className="inline-flex items-center gap-1 rounded-md bg-zinc-900 px-3 py-2 text-sm font-medium text-white"><Plus size={14} /> Add</button>
            </div>
          </div>

          {/* Notes */}
          <div className="dt-card p-6">
            <h2 className="text-sm font-semibold tracking-tight text-zinc-900">Notes</h2>
            <textarea
              data-testid="project-detail-notes"
              rows={6}
              value={project.notes || ""}
              onChange={(e) => setProject({ ...project, notes: e.target.value })}
              onBlur={(e) => update({ notes: e.target.value })}
              placeholder="Write project notes, decisions, scope, links…"
              className="mt-3 w-full rounded-md border border-zinc-200 bg-white px-3 py-2 text-sm leading-relaxed focus:border-[#0066ff] focus:outline-none focus:ring-2 focus:ring-[#0066ff]/20"
            />
          </div>

          {/* Attachments */}
          <Attachments project={project} onReload={load} />

          {/* Follow-up */}
          <div className="dt-card p-6">
            <div className="flex items-center gap-2">
              <BellRing size={16} className="text-amber-500" />
              <h2 className="text-sm font-semibold tracking-tight text-zinc-900">Schedule a Follow-Up</h2>
            </div>
            <p className="mt-1 text-xs text-zinc-500">You'll get a notification on your dashboard on the day you choose.</p>
            <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
              <Field label="Follow-up Date">
                <input
                  data-testid="project-detail-followup-date"
                  type="date"
                  value={project.follow_up_date || ""}
                  onChange={(e) => update({ follow_up_date: e.target.value })}
                  className={inputCls}
                />
              </Field>
              <Field label="Reminder Message">
                <input
                  data-testid="project-detail-followup-note"
                  value={project.follow_up_note || ""}
                  onChange={(e) => setProject({ ...project, follow_up_note: e.target.value })}
                  onBlur={(e) => update({ follow_up_note: e.target.value })}
                  placeholder="e.g. Check in with client on revisions"
                  className={inputCls}
                />
              </Field>
            </div>
            {project.follow_up_date && (
              <div className="mt-3 text-xs text-zinc-600">
                {project.follow_up_dismissed
                  ? <span className="text-zinc-400">Dismissed.</span>
                  : <>Reminder scheduled for <strong>{format(parseISO(project.follow_up_date), "EEEE, MMM d")}</strong>.</>
                }
              </div>
            )}
          </div>
        </div>

        {/* Activity */}
        <div className="dt-card p-6">
          <div className="flex items-center gap-2">
            <Activity size={16} className="text-[#0066ff]" />
            <h2 className="text-sm font-semibold tracking-tight text-zinc-900">Activity</h2>
          </div>
          <div className="relative mt-5 pl-4 before:absolute before:left-1 before:top-1 before:bottom-1 before:w-px before:bg-zinc-200">
            {activity.length === 0 && <div className="text-xs text-zinc-500">No activity yet.</div>}
            {activity.map((a, i) => (
              <div key={a.id || i} className="relative mb-5 last:mb-0">
                <span className="absolute -left-[18px] top-1 h-2.5 w-2.5 rounded-full border-2 border-white bg-[#0066ff]" />
                <div className="text-xs text-zinc-500">{a.created_at ? format(parseISO(a.created_at), "MMM d, p") : ""}</div>
                <div className="mt-0.5 text-sm text-zinc-800">
                  <span className="font-medium">{a.user_name || "Someone"}</span>{" "}
                  <span className="text-zinc-600">{a.action}{a.detail ? ` — ${a.detail}` : ""}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

const inputCls = "w-full rounded-md border border-zinc-200 bg-white px-3 py-2 text-sm placeholder:text-zinc-400 focus:border-[#0066ff] focus:outline-none focus:ring-2 focus:ring-[#0066ff]/20";

function Field({ label, children }) {
  return (
    <label className="flex flex-col gap-1.5 text-sm">
      <span className="text-xs font-medium text-zinc-600">{label}</span>
      {children}
    </label>
  );
}

function fmtBytes(n) {
  if (!n && n !== 0) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function Attachments({ project, onReload }) {
  const fileRef = React.useRef(null);
  const [uploading, setUploading] = React.useState(false);
  const attachments = project.attachments || [];

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/projects/${project.id}/attachments`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Uploaded");
      onReload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const download = async (att) => {
    try {
      const res = await api.get(`/files/${att.file_id}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = att.name || "attachment";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Download failed");
    }
  };

  const remove = async (att) => {
    if (!window.confirm(`Delete "${att.name}"?`)) return;
    try {
      await api.delete(`/projects/${project.id}/attachments/${att.file_id}`);
      toast.success("Removed");
      onReload();
    } catch {
      toast.error("Failed to delete");
    }
  };

  return (
    <div data-testid="project-attachments" className="dt-card p-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Paperclip size={14} className="text-zinc-500" />
          <h2 className="text-sm font-semibold tracking-tight text-zinc-900">Attachments</h2>
          <span className="rounded-full bg-zinc-100 px-2 py-0.5 text-[10px] font-semibold text-zinc-600">{attachments.length}</span>
        </div>
        <button
          data-testid="project-attachment-upload-btn"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          className="inline-flex items-center gap-1.5 rounded-md border border-zinc-200 bg-white px-3 py-1.5 text-xs font-medium text-zinc-700 hover:border-[#0066ff] hover:text-[#0066ff] disabled:opacity-50"
        >
          <Upload size={12} /> {uploading ? "Uploading…" : "Upload"}
        </button>
        <input
          ref={fileRef}
          data-testid="project-attachment-file"
          type="file"
          onChange={upload}
          className="hidden"
        />
      </div>
      <div className="mt-4 flex flex-col gap-2">
        {attachments.length === 0 ? (
          <div className="rounded-md border border-dashed border-zinc-200 px-4 py-6 text-center text-xs text-zinc-400">
            No documents attached yet. Upload contracts, briefs, references, anything you want next to the project.
          </div>
        ) : attachments.map((att) => (
          <div
            key={att.file_id}
            data-testid={`project-attachment-${att.file_id}`}
            className="group flex items-center gap-3 rounded-md border border-zinc-100 bg-white px-3 py-2 transition-colors hover:border-zinc-200 hover:bg-zinc-50/60"
          >
            <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md bg-[#E5F0FF] text-[#0066ff]">
              <FileText size={14} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-medium text-zinc-900">{att.name}</div>
              <div className="text-[11px] text-zinc-500">
                {fmtBytes(att.size)}
                {att.uploaded_at && ` · ${format(parseISO(att.uploaded_at), "MMM d, yyyy")}`}
              </div>
            </div>
            <button
              onClick={() => download(att)}
              className="rounded p-1.5 text-zinc-500 hover:bg-white hover:text-[#0066ff]"
              title="Download"
            >
              <Download size={13} />
            </button>
            <button
              onClick={() => remove(att)}
              className="rounded p-1.5 text-zinc-400 hover:bg-red-50 hover:text-red-600 opacity-0 transition-opacity group-hover:opacity-100"
              title="Delete"
            >
              <Trash2 size={13} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
